import {
  IStyles,
  PrimaryButton,
  pxToRem,
  pxTovW,
  useCommonServiceClientContext,
} from '@geneo2-web/shared-ui';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CloseIcon from '@mui/icons-material/Close';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import {
  Box,
  Checkbox,
  CircularProgress,
  IconButton,
  Typography,
} from '@mui/material';
import { SectionStudentGroup } from '@protos/learning_management/lms.assessment.teacher.apis_pb';
import { useEffect, useState } from 'react';

/** Server default; also the cap the panel pages through the list with. */
const PAGE_SIZE = 10;

const styles: IStyles = {
  root: {
    width: { xs: '100%', md: pxToRem(480), lg: pxTovW(620) },
    border: '1px solid #CCE6FE',
    borderRadius: { xs: pxToRem(10), md: pxToRem(10), lg: pxTovW(10) },
    padding: { xs: pxToRem(12), md: pxToRem(16), lg: pxTovW(16) },
    boxSizing: 'border-box',
    mt: { xs: pxToRem(12), md: pxToRem(12), lg: pxTovW(12) },
    backgroundColor: '#F6FBFF',
  },
  headingBox: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    mb: { xs: pxToRem(10), md: pxToRem(12), lg: pxTovW(12) },
  },
  listBox: {
    maxHeight: { xs: pxToRem(300), md: pxToRem(340), lg: pxTovW(360) },
    overflowY: 'auto',
    overflowX: 'hidden',
    '&::-webkit-scrollbar': { display: 'none' },
  },
  groupCard: {
    border: '1px solid #CCE6FE',
    borderRadius: { xs: pxToRem(10), md: pxToRem(10), lg: pxTovW(10) },
    mb: { xs: pxToRem(10), md: pxToRem(10), lg: pxTovW(10) },
    backgroundColor: 'common.white',
    overflow: 'hidden',
  },
  groupHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: { xs: pxToRem(6), md: pxToRem(8), lg: pxTovW(8) },
    padding: { xs: pxToRem(10), md: pxToRem(12), lg: pxTovW(12) },
  },
  studentRow: {
    display: 'flex',
    alignItems: 'center',
    gap: { xs: pxToRem(6), md: pxToRem(8), lg: pxTovW(8) },
    padding: {
      xs: `${pxToRem(6)} ${pxToRem(10)} ${pxToRem(6)} ${pxToRem(28)}`,
      md: `${pxToRem(8)} ${pxToRem(12)} ${pxToRem(8)} ${pxToRem(30)}`, lg: `${pxTovW(8)} ${pxTovW(12)} ${pxTovW(8)} ${pxTovW(40)}`,
    },
    borderTop: '1px solid #EEF6FF',
  },
  footerBox: {
    display: 'flex',
    flexDirection: 'column',
    gap: { xs: pxToRem(10), md: pxToRem(12), lg: pxTovW(12) },
    mt: { xs: pxToRem(12), md: pxToRem(14), lg: pxTovW(14) },
  },
};

interface IPickedGroup {
  groupName: string;
  sectionId: bigint;
  studentIds: string[];
}

interface IProps {
  /** Panel renders (and refetches) only while true. */
  open: boolean;
  onClose: () => void;
  /** The section this project assigns to; previous groups are listed for it. */
  sectionId?: bigint;
  /** Names already used on the assign screen - offered ones are disabled. */
  existingGroupNames?: string[];
  /** Students already placed in a group - not selectable here. */
  unavailableStudentIds?: Set<string>;
  onAddGroups: (groups: IPickedGroup[]) => void;
  /** Surfaces validation problems to the caller's toast. */
  onValidationError?: (message: string) => void;
}

export const PreviousGroupsPanel = (props: IProps) => {
  const {
    open,
    onClose,
    sectionId,
    existingGroupNames = [],
    unavailableStudentIds,
    onAddGroups,
    onValidationError,
  } = props;
  const { LmsAssessmentTeacherAPIServiceV2WithStatusCodeHandler } =
    useCommonServiceClientContext();

  const [groups, setGroups] = useState<SectionStudentGroup[]>([]);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [status, setStatus] = useState<'idle' | 'loading' | 'error'>('idle');
  /**
   * Keyed by the group's row index, not its id - the same student can sit in
   * several previous groups, and the API may report them all with id 0, so the
   * id is not a safe key for per-group selection.
   */
  const [selected, setSelected] = useState<Record<number, string[]>>({});
  const [expanded, setExpanded] = useState<Record<number, boolean>>({});

  const fetchGroups = async (nextPage: number) => {
    if (!sectionId) return;
    try {
      setStatus('loading');
      const response =
        await LmsAssessmentTeacherAPIServiceV2WithStatusCodeHandler.getTeacherSectionGroups(
          { sectionId, limit: PAGE_SIZE, page: nextPage }
        );
      const fetched = response.data?.groups ?? [];
      setGroups((current) =>
        nextPage === 1 ? fetched : [...current, ...fetched]
      );
      setHasMore(response.data?.hasMore ?? false);
      setPage(nextPage);
      setStatus('idle');
    } catch (error) {
      console.log(error);
      setStatus('error');
    }
  };

  // Fresh list every time the panel opens or the target section changes.
  useEffect(() => {
    if (!open || !sectionId) return;
    setGroups([]);
    setSelected({});
    setExpanded({});
    setHasMore(false);
    setPage(1);
    fetchGroups(1);
  }, [open, sectionId]);

  if (!open) return null;

  // studentId -> the group row that has currently picked it. A student can be
  // added by only one group, so it is locked out of every other group here.
  const claimedBy = new Map<string, number>();
  Object.entries(selected).forEach(([rowIndex, ids]) => {
    ids.forEach((id) => claimedBy.set(id, Number(rowIndex)));
  });

  const isNameTaken = (group: SectionStudentGroup) =>
    existingGroupNames.some(
      (name) =>
        name.trim().toLowerCase() === group.groupName.trim().toLowerCase()
    );

  /** A student this group could still contribute: not taken on the assign
   * screen, and not already picked by another group in this panel. */
  const selectableIds = (group: SectionStudentGroup, rowIndex: number) =>
    group.students
      .map((student) => student.studentId.toString())
      .filter(
        (id) =>
          !unavailableStudentIds?.has(id) &&
          (!claimedBy.has(id) || claimedBy.get(id) === rowIndex)
      );

  // The whole group is off-limits when its name clashes, or every member is
  // already placed on the assign screen - the server rejects both.
  const groupDisabledReason = (group: SectionStudentGroup) => {
    if (isNameTaken(group)) return 'Already added';
    const takenOnScreen = group.students.every((student) =>
      unavailableStudentIds?.has(student.studentId.toString())
    );
    if (takenOnScreen) return 'All students in a group';
    return undefined;
  };

  const pickedIds = (rowIndex: number) => selected[rowIndex] ?? [];

  const toggleStudent = (
    group: SectionStudentGroup,
    rowIndex: number,
    studentId: string
  ) => {
    if (groupDisabledReason(group)) return;
    if (unavailableStudentIds?.has(studentId)) return;
    // Locked because another group in this panel already picked this student.
    if (claimedBy.has(studentId) && claimedBy.get(studentId) !== rowIndex)
      return;
    setSelected((prev) => {
      const current = prev[rowIndex] ?? [];
      const next = current.includes(studentId)
        ? current.filter((id) => id !== studentId)
        : [...current, studentId];
      return { ...prev, [rowIndex]: next };
    });
  };

  const toggleGroupAll = (group: SectionStudentGroup, rowIndex: number) => {
    if (groupDisabledReason(group)) return;
    const ids = selectableIds(group, rowIndex);
    setSelected((prev) => {
      const current = prev[rowIndex] ?? [];
      const allPicked =
        ids.length > 0 && ids.every((id) => current.includes(id));
      return { ...prev, [rowIndex]: allPicked ? [] : ids };
    });
  };

  const toggleExpanded = (rowIndex: number) =>
    setExpanded((prev) => ({ ...prev, [rowIndex]: !prev[rowIndex] }));

  const totalSelected = Object.values(selected).reduce(
    (sum, ids) => sum + ids.length,
    0
  );

  const handleAdd = () => {
    const picked: IPickedGroup[] = groups
      .map((group, rowIndex) => ({
        groupName: group.groupName.trim(),
        sectionId: group.sectionId,
        studentIds: pickedIds(rowIndex),
      }))
      .filter((group) => group.studentIds.length > 0);
    if (picked.length === 0) {
      onValidationError?.('Select at least one student to add.');
      return;
    }
    onAddGroups(picked);
  };

  return (
    <Box sx={styles.root}>
      <Box sx={styles.headingBox}>
        <Typography variant="h4" fontWeight="bold">
          Previous Groups
        </Typography>
        <IconButton
          size="small"
          onClick={onClose}
          aria-label="Close previous groups"
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>

      <Box sx={styles.listBox}>
        {status === 'loading' && groups.length === 0 ? (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              py: { xs: pxToRem(24), md: pxToRem(24), lg: pxTovW(30) },
            }}
          >
            <CircularProgress size={26} />
          </Box>
        ) : status === 'error' && groups.length === 0 ? (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: pxToRem(10),
              py: { xs: pxToRem(24), md: pxToRem(24), lg: pxTovW(30) },
            }}
          >
            <Typography variant="h5">Could not load previous groups.</Typography>
            <PrimaryButton
              onClick={() => fetchGroups(1)}
              sx={{ width: 'max-content' }}
            >
              Retry
            </PrimaryButton>
          </Box>
        ) : groups.length === 0 ? (
          <Typography
            variant="h5"
            sx={{
              textAlign: 'center',
              py: { xs: pxToRem(24), md: pxToRem(24), lg: pxTovW(30) },
              color: 'text.secondary',
            }}
          >
            No previous groups for this section.
          </Typography>
        ) : (
          groups.map((group, rowIndex) => {
            const disabledReason = groupDisabledReason(group);
            const picked = pickedIds(rowIndex);
            const selectable = selectableIds(group, rowIndex);
            const allPicked =
              selectable.length > 0 &&
              selectable.every((id) => picked.includes(id));
            const isOpen = !!expanded[rowIndex];
            return (
              <Box
                key={rowIndex}
                sx={{
                  ...styles.groupCard,
                  opacity: disabledReason ? 0.5 : 1,
                  borderColor: picked.length ? '#34A853' : '#CCE6FE',
                }}
              >
                <Box sx={styles.groupHeader}>
                  <Checkbox
                    checked={allPicked}
                    indeterminate={picked.length > 0 && !allPicked}
                    disabled={!!disabledReason}
                    disableRipple
                    icon={
                      <RadioButtonUncheckedIcon sx={{ color: '#02FC73' }} />
                    }
                    checkedIcon={<CheckCircleIcon color="success" />}
                    onChange={() => toggleGroupAll(group, rowIndex)}
                    sx={{ p: 0 }}
                  />
                  <Box
                    sx={{
                      flexGrow: 1,
                      minWidth: 0,
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: pxToRem(8),
                      flexWrap: 'wrap',
                    }}
                    onClick={() => toggleExpanded(rowIndex)}
                  >
                    <Typography variant="h5" fontWeight="bold">
                      {group.groupName} ({picked.length}/{group.students.length})
                    </Typography>
                    {disabledReason && (
                      <Typography variant="smallText" color="primary">
                        {disabledReason}
                      </Typography>
                    )}
                  </Box>
                  <IconButton
                    size="small"
                    onClick={() => toggleExpanded(rowIndex)}
                    aria-label={isOpen ? 'Collapse group' : 'Expand group'}
                  >
                    {isOpen ? (
                      <ExpandLessIcon fontSize="small" />
                    ) : (
                      <ExpandMoreIcon fontSize="small" />
                    )}
                  </IconButton>
                </Box>

                {isOpen &&
                  group.students.map((student) => {
                    const studentId = student.studentId.toString();
                    const takenOnScreen = unavailableStudentIds?.has(studentId);
                    const claimedElsewhere =
                      claimedBy.has(studentId) &&
                      claimedBy.get(studentId) !== rowIndex;
                    const locked =
                      !!disabledReason || takenOnScreen || claimedElsewhere;
                    return (
                      <Box
                        key={studentId}
                        sx={{
                          ...styles.studentRow,
                          opacity: locked ? 0.5 : 1,
                          cursor: locked ? 'not-allowed' : 'pointer',
                        }}
                        onClick={() =>
                          toggleStudent(group, rowIndex, studentId)
                        }
                      >
                        <Checkbox
                          checked={picked.includes(studentId)}
                          disabled={locked}
                          disableRipple
                          tabIndex={-1}
                          icon={
                            <RadioButtonUncheckedIcon
                              sx={{ color: '#02FC73' }}
                            />
                          }
                          checkedIcon={<CheckCircleIcon color="success" />}
                          sx={{ p: 0, pointerEvents: 'none' }}
                        />
                        <Typography
                          variant="h5"
                          sx={{ flexGrow: 1, minWidth: 0 }}
                        >
                          {student.studentName ||
                            `Roll ${student.rollNumber || '-'}`}
                        </Typography>
                        {!!student.rollNumber && (
                          <Typography variant="smallText" color="primary">
                            Roll No: {student.rollNumber}
                          </Typography>
                        )}
                        {claimedElsewhere && !takenOnScreen && (
                          <Typography variant="smallText" color="primary">
                            In another group
                          </Typography>
                        )}
                        {takenOnScreen && (
                          <Typography variant="smallText" color="primary">
                            In a group
                          </Typography>
                        )}
                      </Box>
                    );
                  })}
              </Box>
            );
          })
        )}
      </Box>

      <Box sx={styles.footerBox}>
        {hasMore && (
          <PrimaryButton
            onClick={() => fetchGroups(page + 1)}
            disabled={status === 'loading'}
            sx={{
              width: '100%',
              backgroundColor: 'common.white',
              color: 'secondary.main',
              border: '1px solid',
              borderColor: 'secondary.main',
              boxShadow: 'none',
            }}
          >
            {status === 'loading' ? 'Loading...' : 'View More'}
          </PrimaryButton>
        )}
        <PrimaryButton
          onClick={handleAdd}
          disabled={totalSelected === 0}
          sx={{ width: '100%' }}
        >
          <Typography variant="h4" color="white" fontWeight="bold">
            ADD SELECTED {totalSelected > 0 && `(${totalSelected})`}
          </Typography>
        </PrimaryButton>
      </Box>
    </Box>
  );
};
